import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { prospectStatusLabel } from '../../utils/prospectStatus';
import { computeProspectCompletionRate } from '../../utils/analytics';
import StateSlideCarousel from './StateSlideCarousel';

const TONE_BAR_STYLES = {
  slate: { dot: 'bg-secondary-500', bar: 'from-secondary-400 to-secondary-600', value: 'text-secondary-600' },
  sky: { dot: 'bg-sky-500', bar: 'from-sky-400 to-sky-600', value: 'text-sky-600' },
  violet: { dot: 'bg-violet-500', bar: 'from-violet-400 to-violet-600', value: 'text-violet-600' },
  amber: { dot: 'bg-amber-500', bar: 'from-amber-400 to-amber-600', value: 'text-amber-600' },
  emerald: { dot: 'bg-emerald-500', bar: 'from-emerald-400 to-emerald-600', value: 'text-emerald-600' },
  indigo: { dot: 'bg-indigo-500', bar: 'from-indigo-400 to-indigo-600', value: 'text-indigo-600' },
  rose: { dot: 'bg-rose-500', bar: 'from-rose-400 to-rose-600', value: 'text-rose-600' },
  orange: { dot: 'bg-orange-500', bar: 'from-orange-400 to-orange-600', value: 'text-orange-600' },
};

function ProspectStateRow({ row, percentage, styles, centered = false }) {
  return (
    <div className={centered ? 'mx-auto w-full max-w-sm text-center' : undefined}>
      <div className={`mb-2 flex items-center ${centered ? 'justify-center gap-2' : 'justify-between'}`}>
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${styles.dot}`} />
          <span className="text-sm font-medium text-secondary-700 ">
            {row.label}
          </span>
        </div>
        {!centered ? (
          <div className="flex items-baseline gap-2">
            <span className={`text-lg font-bold tabular-nums ${styles.value}`}>{row.count}</span>
            <span className="rounded-md bg-secondary-100/80 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-secondary-500  ">
              {percentage}%
            </span>
          </div>
        ) : null}
      </div>
      {centered ? (
        <p className="mb-2 text-lg font-bold tabular-nums text-secondary-900 ">
          <span className={styles.value}>{row.count}</span>
          <span className="ml-2 text-sm font-normal text-secondary-500 ">
            ({percentage}%)
          </span>
        </p>
      ) : null}
      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary-100/80 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]">
        <div
          className={`h-2 rounded-full bg-gradient-to-r ${styles.bar} transition-[width] duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function ProspectStateDistributionCard({
  title,
  stateData = {},
  prospectStatuses = [],
  t,
  delay = 0.05,
  carousel = false,
}) {
  const normalizedStateData = useMemo(() => {
    const normalized = {};
    Object.entries(stateData || {}).forEach(([code, count]) => {
      const key = String(code).trim().toUpperCase();
      if (!key) return;
      normalized[key] = (normalized[key] || 0) + (count || 0);
    });
    return normalized;
  }, [stateData]);

  const rows = useMemo(() => {
    const active = prospectStatuses.filter((s) => s.is_active !== false);
    const statuses = active.length > 0 ? active : prospectStatuses;
    const seen = new Set();
    return statuses.reduce((acc, status) => {
      const code = String(status.code || '').trim().toUpperCase();
      if (!code || seen.has(code)) {
        return acc;
      }
      seen.add(code);
      acc.push({
        code,
        label: prospectStatusLabel(status.code, t, prospectStatuses),
        tone: status.badge_tone || 'slate',
        count: normalizedStateData[code] || 0,
      });
      return acc;
    }, []);
  }, [prospectStatuses, normalizedStateData, t]);

  const total = useMemo(() => rows.reduce((sum, row) => sum + row.count, 0), [rows]);

  const carouselItems = useMemo(
    () => rows.map((row) => {
      const styles = TONE_BAR_STYLES[row.tone] || TONE_BAR_STYLES.slate;
      const percentage = total > 0 ? ((row.count / total) * 100).toFixed(1) : '0.0';
      return { key: row.code, row, percentage, styles };
    }),
    [rows, total]
  );

  const completionRate = useMemo(
    () => computeProspectCompletionRate(normalizedStateData, prospectStatuses),
    [normalizedStateData, prospectStatuses]
  );

  const completionLabel = t?.vendor?.completionRate
    || t?.admin?.completionRate
    || 'Completion Rate';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={carousel ? 'card-premium flex min-h-[6.5rem] items-center justify-center p-4' : 'card-premium p-6'}
    >
      {!carousel && title ? (
        <h3 className="mb-6 flex items-center text-lg font-semibold text-secondary-900 ">
          <ClipboardDocumentListIcon className="mr-2 h-5 w-5 text-orange-500" />
          {title}
        </h3>
      ) : null}

      {rows.length === 0 ? (
        <p className="text-sm text-secondary-500 ">
          {t?.vendor?.prospects?.noProspectStatuses || 'No prospect statuses configured.'}
        </p>
      ) : carousel ? (
        <StateSlideCarousel
          className="w-full"
          items={carouselItems}
          renderItem={(item) => (
            <ProspectStateRow
              row={item.row}
              percentage={item.percentage}
              styles={item.styles}
              centered
            />
          )}
        />
      ) : (
        <div className="space-y-4">
          {carouselItems.map((item) => (
            <ProspectStateRow
              key={item.key}
              row={item.row}
              percentage={item.percentage}
              styles={item.styles}
            />
          ))}
        </div>
      )}

      {rows.length > 0 && !carousel ? (
        <div className="mt-6 border-t border-secondary-200 pt-4 ">
          <div className="flex items-center justify-between text-sm">
            <span className="text-secondary-600 ">{completionLabel}</span>
            <span className="text-lg font-bold text-success-600">
              {completionRate.toFixed(1)}%
            </span>
          </div>
        </div>
      ) : null}
    </motion.div>
  );
}

export default ProspectStateDistributionCard;
